"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = exports.UX_FLYOUT_PLACEMENTS = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _react = _interopRequireWildcard(require("react"));
var _reactDom = _interopRequireDefault(require("react-dom"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _classnames = _interopRequireDefault(require("classnames"));
var _flip = _interopRequireDefault(require("./middleware/flip"));
var _stretch = _interopRequireDefault(require("./middleware/stretch"));
var _reactDom2 = require("@floating-ui/react-dom");
var _utils = require("./utils");
const _excluded = ["middlewareData", "refs"];
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && Object.prototype.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { (0, _defineProperty2.default)(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
const UX_FLYOUT_CLASSNAME = 'ux-flyout';
const UX_FLYOUT_PLACEMENTS = exports.UX_FLYOUT_PLACEMENTS = ['bottom-end', 'bottom-start', 'bottom', 'left-end', 'left-start', 'left', 'right-end', 'right-start', 'right', 'top-end', 'top-start', 'top'];

/**
 * Determine if the element is visible on the page
 * HTMLElement.checkVisibility is still too new to use
 * @param {HTMLElement} el HTML element
 * @returns {Boolean} is the HTML Element visible
 */
function isVisible(el) {
  let styles;
  try {
    styles = getComputedStyle(el);
  } catch {
    return false;
  }
  return el.getClientRects().length > 0 && parseInt(styles.opacity, 10) > 0 && styles.visibility === 'visible';
}

/**
 * Distance away from the edges that the arrow can travel.
 * This _should_ be the border-radius of the flyout however,
 * that value is themable so we assume the value wont be too big.
 */
const ARROW_EDGE_OFFSET_INT = 4;

/**
 * Creates an arrow for the flyout
 *
 * @param {Object} props - Props for the component
 * @param {Object} ref - The reference to the component element
 * @returns {Component} - Arrow component
 */
const Arrow = /*#__PURE__*/_react.default.forwardRef((props, ref) => {
  return /*#__PURE__*/_react.default.createElement("div", {
    className: `${UX_FLYOUT_CLASSNAME}-arrow-reference`,
    ref: ref
  });
});
Arrow.propTypes = {
  /** Provides placement for the arrow */
  arrow: _propTypes.default.shape({
    x: _propTypes.default.number,
    y: _propTypes.default.number
  })
};

/**
 * Creates a Flyout component
 *
 * @param {Object} props - Properties for the Flyout element
 * @param {Element} props.anchorRef - Reference to a DOM element where the flyout should be visually attached.
 * @param {Boolean} props.arrow - Boolean which determines if an arrow should be included on the flyout
 * @param {Node|Array<Node>} props.chilren - Children to render inside the flyout
 * @param {String} props.className - Optional classname for positioning
 * @param {Boolean} props.flip - Allows the position to flip when space is limited
 * @param {String} props.id - Id to set on the flyout for accessibilty
 * @param {String} props.placement - Configuration for setting the placement of the flyout relative to the anchor.anchorRef
 * @param {String} props.strategy - Sets the strategy for @floating-ui (absolute/fixed)
 * @param {Element} props.portalElem - Optional react element to create a react portal from
 * @param {Array<Object>} props.middleware - Middleware for customizing behavior and positioning, see https://floating-ui.com/docs/middleware
 * @param {String} props.role - Role to set on the flyout for accessibility
 * @param {Function} props.onClose - Fires when the user attempts to escape the flyout
 * @param {Function} props.onPlacement - Fires when the placement of the flyout changes
 * @param {Boolean} props.stretch - Stretches the flyout to the nearest relative ancestor
 * @param {String} props.offset - Sets the offset for @floating-ui (sm/lg)
 * @param {Boolean} props.resize - Determines whether to resize the flyout to fit viewport
 * @returns {Component} - Flyout element
 */
function Flyout(_ref) {
  let {
    anchorRef,
    arrow: arrowConfig,
    children,
    className,
    id,
    middleware: middlewareProps = [],
    placement,
    strategy,
    portalElem,
    role,
    flip: shouldFlip = true,
    stretch: shouldStretch = false,
    onClose = Function.prototype,
    onPlacement = Function.prototype
  } = _ref;
  const arrowRef = (0, _react.useRef)(null);
  // Arrow middleware should go towards the end of the array, per https://floating-ui.com/docs/arrow#order
  const middleware = [_utils.overflowDetectionMiddleware, ...middlewareProps,
  // Legacy support for flip and stretch properties since we did not have the middleware prop before
  // and did not announce the change.
  // https://github.com/gdcorp-uxp/uxcore2/pull/1964
  ...(shouldFlip && !middlewareProps.length ? [(0, _flip.default)()] : []), ...(shouldStretch && !middlewareProps.length ? [(0, _stretch.default)()] : []),
  // Shifting has to happen after flipping for correct positioning.
  (0, _reactDom2.shift)(), (0, _reactDom2.arrow)({
    element: arrowRef,
    padding: ARROW_EDGE_OFFSET_INT
  })];
  const _useFloating = (0, _reactDom2.useFloating)({
      // https://floating-ui.com/docs/react-dom#updating
      whileElementsMounted: function () {
        try {
          for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
            args[_key] = arguments[_key];
          }
          return (0, _reactDom2.autoUpdate)(...args, {
            animationFrame: true
          });
        } catch (err) {
          return Function.prototype;
        }
      },
      placement,
      strategy,
      middleware
    }),
    {
      // middlewareData contains information returned from the middleware, used for additional attributes
      middlewareData,
      // refs of the elements in the DOM
      refs
      // floatingData contains data which allows the flyout to be positioned
    } = _useFloating,
    floatingData = (0, _objectWithoutProperties2.default)(_useFloating, _excluded);

  // Using a virtual element approach for positioning to avoid rendering a reference element inside the component
  // https://floating-ui.com/docs/react-dom#virtual-element
  (0, _react.useLayoutEffect)(() => {
    var _anchorRef$current;
    if (!(anchorRef !== null && anchorRef !== void 0 && (_anchorRef$current = anchorRef.current) !== null && _anchorRef$current !== void 0 && _anchorRef$current.getBoundingClientRect)) return;
    refs.setReference(anchorRef.current);
  }, [refs.setReference, anchorRef, children]);

  // Handle events that occur outside of the flyout context
  (0, _react.useEffect)(() => {
    var _anchorRef$current2;
    if (!(anchorRef !== null && anchorRef !== void 0 && (_anchorRef$current2 = anchorRef.current) !== null && _anchorRef$current2 !== void 0 && _anchorRef$current2.getBoundingClientRect) || !isVisible(refs.floating.current)) return;
    return (0, _utils.outsideListeners)([anchorRef.current, refs.floating.current], onClose);
  }, [anchorRef, refs.floating, onClose]);

  // Fire event if the placement changes
  (0, _react.useLayoutEffect)(() => {
    onPlacement(floatingData.placement);
  }, [floatingData.placement, onPlacement]);

  // Do not render if no anchorRef or children
  if (!anchorRef || !children) {
    return null;
  }

  // The code explodes if middlewareData is accessed directly ???
  const {
    arrow: arrowPosition
  } = _objectSpread({}, middlewareData);
  const arrowStyles = {
    size: (0, _utils.getArrowSize)(arrowConfig),
    top: arrowPosition === null || arrowPosition === void 0 ? void 0 : arrowPosition.y,
    left: arrowPosition === null || arrowPosition === void 0 ? void 0 : arrowPosition.x
  };

  /**
   * Creates a single use event listener to listen for outside focus for proper onClose invocation.
   */
  function focusEventListener() {
    if (!isVisible(refs.floating.current)) return;
    const onFocusBound = ev => {
      if ((0, _utils.isEventOutside)(ev, [refs.floating.current, anchorRef.current])) onClose(ev);
    };
    document.addEventListener('focus', onFocusBound, {
      capture: true,
      once: true
    });
  }
  const flyout = /*#__PURE__*/_react.default.createElement("div", (0, _extends2.default)({
    "data-version": "2400",
    onFocus: focusEventListener,
    className: (0, _classnames.default)(UX_FLYOUT_CLASSNAME, className),
    id: id,
    role: role,
    ref: refs.setFloating,
    style: (0, _utils.getFlyoutStyle)(_objectSpread({
      arrow: arrowConfig && arrowStyles
    }, floatingData))
  }, (0, _utils.getFlyoutAttributes)(_objectSpread(_objectSpread({}, middlewareData), floatingData))), children, arrowConfig && /*#__PURE__*/_react.default.createElement(Arrow, {
    ref: arrowRef
  }));
  return portalElem ? /*#__PURE__*/_reactDom.default.createPortal(flyout, portalElem) : flyout;
}
Flyout.propTypes = {
  /** Reference to a DOM element where the flyout should be visually attached. The flyout will not render without this */
  anchorRef: _propTypes.default.oneOfType([_propTypes.default.func, _propTypes.default.shape({
    current: _propTypes.default.object
  })]),
  /** Determines if an arrow should be included on the flyout, if a string is provided, set the size of the arrow */
  arrow: _propTypes.default.oneOfType([_propTypes.default.bool, _propTypes.default.string]),
  /** Children to render within the flyout */
  children: _propTypes.default.oneOfType([_propTypes.default.arrayOf(_propTypes.default.node), _propTypes.default.node]),
  /** Id for the flyout, used for accessibility */
  id: _propTypes.default.string,
  /** Placement, for more details see https://floating-ui.com/ */
  placement: _propTypes.default.oneOf(UX_FLYOUT_PLACEMENTS),
  /** Sets the strategy for @floating-ui (absolute/fixed) */
  strategy: _propTypes.default.oneOf(['absolute', 'fixed']),
  /** Role for the flyout, used for accessibility */
  role: _propTypes.default.string,
  /** Fires when the user attempts to escape from the flyout */
  onClose: _propTypes.default.func,
  /** Fires when the flyout position changes */
  onPlacement: _propTypes.default.func,
  /** Optional class name for positioning */
  className: _propTypes.default.string,
  /** Allows the position to flip when space is limited */
  flip: _propTypes.default.bool,
  /** Stretches the flyout to the nearest relative ancestor */
  stretch: _propTypes.default.bool,
  /** Optional react element to create a react portal from  */
  portalElem: _propTypes.default.object,
  /** Middleware for customizing behavior and positioning, see https://floating-ui.com/docs/middleware */
  middleware: _propTypes.default.arrayOf(_propTypes.default.object)
};
Flyout.displayName = 'Flyout';
var _default = exports.default = Flyout;